<template>
    <v-container grid-list-md fluid pa-0 ma-0 mb-4 class="c-flex-c1">

      <!-- item differ -->
      <t-item-diff v-if="$store.state.settings.debugMode" :item1="lastSave" :item2="merged">
        <v-layout 
        slot="item1Header"
        align-center class="subheading font-weight-bold pl-2 crx-toolbar">
          Last save
        </v-layout>
        <v-layout 
        slot="item2Header"
        align-center class="subheading font-weight-bold pl-2 crx-toolbar">
          LIVE
        </v-layout>
      </t-item-diff>

      <!-- EXTRA info for exisiting definition -->
      <v-alert 
      :value="error.length > 0" 
      color="error" icon="warning"
      transition="slide-y-transition"
      >
        Error: {{ error }}
      </v-alert>

      <v-row >

        <v-col md="6">
          <v-card class="crx-section-card">
            <div class="pa-4 subheading font-weight-bold">
              Define
            </div>
            <v-card-text>


              <div 
              class="subheading font-weight-bold mb-2"
              v-if="id">
                ID: {{ id }}
              </div>

              <v-text-field 
              v-model="formData.name" 
              label="Name" 
              required
              counter="200"
              :error-messages="nameErrors"
              @input="$v.formData.name.$touch()"
              @blur="$v.formData.name.$touch()">
              </v-text-field>

              <v-text-field 
              v-model="formData.description" 
              label="Description"
              counter="500"
              :error-messages="descriptionErrors">
              </v-text-field>

              <v-text-field
              v-model="formData.sku" 
              label="SKU"
              counter="100">
              </v-text-field>

              <v-textarea 
              v-model="formData.location" 
              label="Location in store"
              counter="200"
              :error-messages="locationErrors">
              </v-textarea>

              <div>
                Weight
              </div>
              <c-ms-ii
              sid="si-mass-g"
              :key="1"
              ref="weightMetricSelector"
              class="crx-box-input"
              :realValue.sync="formData.weight">
              </c-ms-ii>

            </v-card-text>
          </v-card>

        </v-col>

        <v-col md="6">
          <v-card class="pa-4 crx-section-card">

            <div class="subheading font-weight-bold">
              Which main metric is this item measured in?
            </div>

            <!-- METRIC SELECTION DIV -->
            <v-radio-group v-model="formData.metric" :disabled="disableMetricSelection">
              <v-radio label="Units" :value="0" color="primary"></v-radio>
              <v-radio label="Volume" :value="1" color="primary"></v-radio>
              <v-radio label="Mass" :value="2" color="primary"></v-radio>
              <v-radio label="Length" :value="3" color="primary"></v-radio>
            </v-radio-group>

            <div v-if="id === null">
              <div class="title">
                Does this make sense?
              </div>
              <div class="subheading">
                I have 10 <span class="font-weight-medium color1--text">{{ metricString }}</span> of this item in stock.
              </div>
              <div class="subheading">
                A customer would like to buy 2 <span class="font-weight-medium color1--text">{{ metricString }}</span> of this item.
              </div>

              <v-layout align-start class="pa-3 color1 border-radius-a-13 white--text">
                <v-icon color="white">mdi-information-outline</v-icon>
                <div class="ml-4">
                  You cannot change the default metric after the definition has been created. Please choose the correct option!
                </div>
              </v-layout>
            </div>

          </v-card>
        </v-col>

      </v-row>

      <v-row>
        <v-col md="4" lg="4">
          <v-card>
            <v-layout class="border-b--thin pa-4">
              Item categories
            </v-layout>
            <category-card :selected.sync="formData.categories" :widgetMode="1"></category-card>
          </v-card>
        </v-col>

        <v-col md="4" lg="4">

          <v-card class="pa-4 crx-section-card">

            <div class="subheading font-weight-bold">
              Item flags
            </div>

            <v-checkbox
            v-model="formData.rawMaterial"
            label="Raw material"
            color="blue"
            ></v-checkbox>

            <v-checkbox
            v-model="formData.product"
            label="Finished product"
            color="green"
            ></v-checkbox>

            <v-checkbox
            v-model="formData.disposable"
            label="Disposable"
            color="red darken-3"
            ></v-checkbox>

          </v-card>

        </v-col>

        <v-col lg="4">

          <v-card class="crx-section-card pa-4">
              <!-- CONVERSION TABLE DIV v-if="metric === 1 || metric === 2"-->
              <div>
                <div class="subheading font-weight-bold">
                  Conversion table
                </div>

                <!-- most likely deprecated v-if="false"-->

                <div class="label">
                  Units
                </div>
                <c-ms-ii
                v-if="$accessControl.userHasAccess(0, $store.state.auth.accessControl)"
                sid="si-units-units"
                preventScaleOnCreated
                class="crx-box-input"
                :quantity.sync="formData.units">
                </c-ms-ii>

                <div class="label mt-2">
                  Volume
                </div>
                <c-ms-ii
                :quantity.sync="formData.volume"
                class="crx-box-input"
                preventScaleOnCreated
                :sid.sync="formData.volumeSid">
                </c-ms-ii>

                <div class="label mt-2">
                  Mass
                </div>

                <c-ms-ii
                class="crx-box-input"
                preventScaleOnCreated
                :quantity.sync="formData.mass"
                :sid.sync="formData.massSid">
                </c-ms-ii>

                <div class="label mt-2">
                  Length
                </div>
                <c-ms-ii
                v-if="$accessControl.userHasAccess(0, $store.state.auth.accessControl)"
                class="crx-box-input"
                preventScaleOnCreated
                :quantity.sync="formData.length"
                :sid.sync="formData.lengthSid"
                >
                </c-ms-ii>

                *In general the ratio of liquids / mass is around 1ml = 1g
              </div>
          </v-card>
        </v-col>

        <v-col md="4">
          <v-card>
            <v-row class="pa-4 border-b--medium">
              Schema
            </v-row>
            <div class="pa-4">
              <schema-card></schema-card>
            </div>
          </v-card>
        </v-col>
        <v-col md="4">
          <v-card>
            <v-row class="pa-4 border-b--medium">
              Custom Props
            </v-row>
            <div class="pa-4">
              <custom-props :inventory_item_id="id" :schema.sync="json_schema"></custom-props>
            </div>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import CategoryCard from './categories/Expansion'
import SchemaCard from './schema/main'
import CustomProps from './custom-props/main'

export default {
  mixins: [validationMixin],
  props: {
    id: {
      default: null
    }
  },
  components: {
    CategoryCard,
    SchemaCard,
    CustomProps
  },
  data () {
    return {
      formData: {
        name: '',
        metric: 0,
        description: '',
        sku: null,
        location: '',
        weight: 0,
        rawMaterial: false,
        product: false,
        disposable: false,
        units: 1,
        mass: 0,
        massSid: 'si-mass-g',
        volume: 0,
        volumeSid: 'si-vol-ml',
        length: 0,
        lengthSid: 'si-len-cm',
        categories: []
      },
      error: '',
      lastSave: {

      },
      json_schema: null
    }
  },
  watch: {
    id (nv) {
      if (nv !== null) {
        this.edit()
      } else {
        this.new()
      }
    },
    isDirty (nv) {
      this.$emit('update:isDirty', nv)
    }
  },
  computed: {
    merged () {
      return JSON.parse(JSON.stringify(this.formData))
    },
    disableMetricSelection () {
      if (this.$accessControl.userHasAccess(0, this.$store.state.auth.accessControl)) {
        return false
      }
      return this.id !== null
    },
    metricString () {
      switch (this.formData.metric) {
        case 0:
          return 'units'
        case 1:
          return 'litres'
        case 2:
          return 'kilograms'
        case 3:
          return 'metres'
      }
    },
    nameErrors () {
      const errors = []
      if (!this.$v.formData.name.$dirty) return errors
      !this.$v.formData.name.maxLength && errors.push('Name must be less than 200 characters')
      !this.$v.formData.name.required && errors.push('Name is required.')
      return errors
    },
    descriptionErrors () {
      const errors = []
      if (!this.$v.formData.description.$dirty) return errors
      !this.$v.formData.description.maxLength && errors.push('Description must be less than 500 characters')
      return errors
    },
    locationErrors () {
      const errors = []
      if (!this.$v.formData.location.$dirty) return errors
      !this.$v.formData.location.maxLength && errors.push('Location must be less than 200 characters')
      return errors
    },
    /* isDirty () {
      if (JSON.stringify(this.formData) !== this.lastSaved) {
        return true
      }
      return false
    }, */
    isDirty () {
      return !_.isEqual(this.lastSave, this.merged)
    }
  },
  validations: {
    formData: {
      name: { required, maxLength: maxLength(200) },
      description: { maxLength: maxLength(500) },
      location: { maxLength: maxLength(200) }
    }
  },
  methods: {
    undo () {
      this.$emit('undo', 5)
      this.formData = Object.assign(this.formData, this.lastSave)
    },
    new () {
      this.$v.$reset()
      this.formData.name = ''
      this.formData.metric = 0
      this.formData.description = ''
      this.formData.sku = ''
      this.formData.location = ''
      this.formData.rawMaterial = false
      this.formData.product = false
      this.formData.disposable = false
      this.formData.units = 0
      this.formData.volume = 0
      this.formData.volumeSid = 'si-vol-ml'
      this.formData.mass = 0
      this.formData.massSid = 'si-mass-g'
      this.formData.length = 0
      this.formData.lengthSid = 'si-len-cm'

      this.lastSave = JSON.parse(JSON.stringify(this.formData))
    },
    edit () {
      this.loadAPI()
        .then(definition => {
          this.$v.$reset()
          this.formData.name = definition.name
          this.formData.metric = definition.metric
          this.formData.description = definition.description
          this.formData.sku = definition.sku
          this.formData.location = definition.location
          this.formData.weight = definition.weight
          this.$nextTick(() => {
            this.$refs.weightMetricSelector.rescale()
          })
          this.formData.rawMaterial = definition.raw_material > 0 // Simple conversion of int to boolean
          this.formData.product = definition.product > 0
          this.formData.disposable = definition.disposable > 0
          this.formData.units = definition.units
          this.formData.volume = definition.volume
          this.formData.volumeSid = definition.volume_sid
          this.formData.mass = definition.mass
          this.formData.massSid = definition.mass_sid
          this.formData.length = definition.length
          this.formData.lengthSid = definition.length_sid
          
          this.json_schema = JSON.parse(definition.json_schema)

          // Legacy block
          // this prevents metric selectors not showing at all
          // when sids are null. This is fixed in new version
          // just database needs migrating
          if (this.formData.volume === 0) {
            this.formData.volumeSid = 'si-vol-ml'
          }
          if (this.formData.mass === 0) {
            this.formData.massSid = 'si-mass-g'
          }
          if (this.formData.length === 0) {
            this.formData.lengthSid = 'si-len-cm'
          }

          this.lastSave = JSON.parse(JSON.stringify(this.formData))
        })
    },
    loadAPI: _.debounce(function (e) {
      return new Promise((resolve, reject) => {
        this.isLoading = true
        this.$axios.get(`inventory-definitions/${this.id}`)
        .then((response) => {
          resolve(response.data)
        })
        .catch(error => {
          console.log(error)
          this.$root.$errorSnackbar(error)
          reject(error)
        })
        .then(() => {
          this.isLoading = false
        })
      })
    }, 150, {leading: true}),
    validateForm () {
      this.$v.$touch()
      return this.$v.$invalid
      /* if (this.$v.$invalid) {
        // console.log('Invalid')
      } else {
        if (this.id !== null) {
          this.updateItem()
        } else {
          this.createItem()
        }
      } */
    }
  },
  created () {
    if (this.id !== null) {
      this.edit()
    } else {
      this.new()
    }
  }
}
</script>

<style scoped>
.crx-section-card {
  height: 100%;
}
</style>
